import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@workos-inc/authkit-react';
import { Button } from '@cotera/client/v0/components/ui/button';
import { useClient } from '../hooks/use-client';

export const CliLoginPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isLoading } = useAuth();
  const client = useClient();

  const [status, setStatus] = useState<
    'loading' | 'ready' | 'approving' | 'approved' | 'error'
  >('loading');
  const [error, setError] = useState<string | null>(null);

  const deviceCode = searchParams.get('device_code');

  const checkStatus = useCallback(async () => {
    if (deviceCode === null) {
      return;
    }
    const result = await client.u.cli.getDeviceCodeStatus({ deviceCode });
    if (result.isErr()) {
      setStatus('error');
      setError(
        result.error.err.errorType === 'NotFound'
          ? 'Device code not found or expired'
          : 'Failed to check device status'
      );
      return;
    }
    if (result.value.approved) {
      setStatus('approved');
    } else {
      setStatus('ready');
    }
  }, [deviceCode, client]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    // If not logged in, redirect to login with returnTo
    if (user === null) {
      const returnTo = encodeURIComponent(
        `/cli-login?device_code=${deviceCode}`
      );
      navigate(`/login?returnTo=${returnTo}`, { replace: true });
      return;
    }

    // Check device code status
    if (deviceCode) {
      void checkStatus();
    } else {
      setStatus('error');
      setError('Missing device code');
    }
  }, [user, isLoading, deviceCode, navigate, checkStatus]);

  const handleApprove = async () => {
    if (!deviceCode) {
      return;
    }

    setStatus('approving');
    const result = await client.u.cli.approve({ deviceCode });

    if (result.isOk()) {
      setStatus('approved');
      return;
    }

    setStatus('error');
    if (result.error.err.errorType === 'Unauthorized') {
      setError('Not authorized. Please log in again.');
    } else {
      setError(result.error.err.message ?? 'Failed to approve device');
    }
  };

  if (status === 'loading' || isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="text-center max-w-md">
          <h1 className="text-xl font-semibold text-red-600 mb-2">Error</h1>
          <p className="text-muted-foreground">
            {error || 'Something went wrong'}
          </p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => navigate('/')}
          >
            Go to Home
          </Button>
        </div>
      </div>
    );
  }

  if (status === 'approved') {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="text-green-500 text-5xl mb-4">✓</div>
          <h1 className="text-2xl font-semibold text-green-600 mb-4">
            CLI Connected!
          </h1>
          <p className="text-muted-foreground mb-2">
            You have successfully authorized the CLI to access your account.
          </p>
          <p className="text-sm text-muted-foreground">
            You can close this window and return to your terminal.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="text-center max-w-md">
        <h1 className="text-2xl font-semibold mb-4">Connect CLI to Coco?</h1>
        <p className="text-muted-foreground mb-6">
          A CLI tool is requesting access to your Coco account.
          {user?.email && (
            <>
              {' '}
              You will be authorizing as <strong>{user.email}</strong>.
            </>
          )}
        </p>
        <div className="flex gap-3 justify-center">
          <Button variant="outline" onClick={() => navigate('/')}>
            Cancel
          </Button>
          <Button onClick={handleApprove} disabled={status === 'approving'}>
            {status === 'approving' ? 'Connecting...' : 'Connect CLI'}
          </Button>
        </div>
      </div>
    </div>
  );
};
